import BaseApiService from './BaseApiService'

export interface SuggestedVariable {
  name: string
  description: string
  exampleValue: string
  kind: 'scalar' | 'loop' | 'conditional'
}

export interface GenerateTemplateResponse {
  templateText: string
  variables: SuggestedVariable[]
}

export interface FieldMapping {
  variableKey: string
  jsonPath: string
  confidence: number
  reasoning: string
}

export interface MapFieldsResponse {
  mappings: FieldMapping[]
}

export interface VariableHint {
  name: string
  kind: 'scalar' | 'loop' | 'conditional'
}

export interface InferredVariable {
  name: string
  type: string
  description: string
  required: boolean
}

export interface InferSchemaResponse {
  variables: InferredVariable[]
  jsonSchema: string
  samplePayload: string
}

export interface ControlTagSuggestion {
  anchor: string
  tag: string
  reason: string
}

export interface SuggestControlTagsResponse {
  suggestions: ControlTagSuggestion[]
}

export interface StarterTemplateType {
  key: string
  label: string
  description: string
}

export interface StarterTemplateResponse {
  templateText: string
  variables: SuggestedVariable[]
  docType: string
  label: string
}

class AiApiService extends BaseApiService {
  getTemplateTypes(): Promise<{ types: StarterTemplateType[] }> {
    return this.get('/v1/ai/template-types')
  }

  starterTemplate(docType: string): Promise<StarterTemplateResponse> {
    return this.post('/v1/ai/starter-template', { docType })
  }

  generateTemplate(prompt: string): Promise<GenerateTemplateResponse> {
    return this.post('/v1/ai/generate-template', { prompt })
  }

  refineTemplate(templateText: string, instruction: string): Promise<GenerateTemplateResponse> {
    return this.post('/v1/ai/refine-template', { templateText, instruction })
  }

  suggestControlTags(templateText: string): Promise<SuggestControlTagsResponse> {
    return this.post('/v1/ai/suggest-control-tags', { templateText })
  }

  mapFields(payloadJson: string, variableKeys: string[]): Promise<MapFieldsResponse> {
    return this.post('/v1/ai/map-fields', { payloadJson, variableKeys })
  }

  acceptMapping(variableKey: string, jsonPath: string): Promise<void> {
    return this.post('/v1/ai/accept-mapping', { variableKey, jsonPath })
  }

  inferSchema(variables: VariableHint[]): Promise<InferSchemaResponse>
  inferSchema(templateVersionId: string): Promise<InferSchemaResponse>
  inferSchema(arg: VariableHint[] | string): Promise<InferSchemaResponse> {
    if (typeof arg === 'string') {
      return this.post('/v1/ai/infer-schema', { templateVersionId: arg })
    }
    return this.post('/v1/ai/infer-schema', { variables: arg })
  }
}

export const aiApiService = new AiApiService()
